import { PaletteMode } from '@mui/material';
import { createSlice } from '@reduxjs/toolkit';
import {
  kitchenStaffDrawerMenu,
  restaurantDrawerMenu,
  restaurantWaiterDrawerMenu,
} from './drawerMenus';

export type MUIIcon = JSX.Element;

export enum DrawerMenuIconType {
  Kitchens,
  Restaurants,
  Menus,
  RestaurantTables,
  RestaurantUsers,
  MealItems,
  Customizations,
  Orders,
  Settings,
  Profile,
  Receipt,
}

export interface DrawerMenuItem {
  path: string;
  title: string;
  icon: DrawerMenuIconType;
}

export enum UserType {
  KitchenStaff,
  RestaurantWaiter,
  RestaurantManager,
  SuperAdmin,
  Customer,
  None,
}

export interface DrawerMenuState {
  title: string;
  paletteType: PaletteMode;
  menuItems: DrawerMenuItem[];
  userType: UserType;
  isCustomerPage: boolean;
}

const initialState: DrawerMenuState = {
  title: 'SmartRisto',
  paletteType: 'light',
  menuItems: [],
  userType: UserType.None,
  isCustomerPage: false,
};

const guidPattern = '[0-9a-fA-F-]{8,}';
const drawerMenu = (path: string) => {
  switch (true) {
    case new RegExp(
      `/kitchen-staff/restaurants/(${guidPattern})/kitchens/(${guidPattern})`
    ).test(path): {
      const match = path.match(
        new RegExp(`/restaurants/(${guidPattern})/kitchens/(${guidPattern})`)
      );
      if (!match) return [];
      const restaurantId = match[1];
      const kitchenId = match[2];
      return kitchenStaffDrawerMenu(restaurantId, kitchenId);
    }
    case new RegExp(`/restaurant-waiter/restaurants/(${guidPattern})`).test(
      path
    ): {
      const match = path.match(new RegExp(`/restaurants/(${guidPattern})`));
      if (!match) return [];
      const restaurantId = match[1];
      return restaurantWaiterDrawerMenu(restaurantId);
    }
    case new RegExp(`/manager/restaurants/(${guidPattern})`).test(path): {
      const match = path.match(new RegExp(`/restaurants/(${guidPattern})`));
      if (!match) return [];
      const restaurantId = match[1];
      return restaurantDrawerMenu(restaurantId);
    }
    default:
      return [];
  }
};

const isCustomerPage = (path: string) => {
  switch (true) {
    case /\/customer/.test(path): {
      return true;
    }
    default:
      return false;
  }
};

const userType: (path: string) => UserType | null = (path: string) => {
  switch (true) {
    case /\/manager*/.test(path): {
      return UserType.RestaurantManager;
    }
    case /\/kitchen-staff*/.test(path): {
      return UserType.KitchenStaff;
    }
    case /\/restaurant-waiter*/.test(path): {
      return UserType.RestaurantWaiter;
    }
    case /\/account\/(profile|settings).*/.test(path): {
      return null;
    }
    case /\/account\/(login|forgot-password|verify-email).*/.test(path): {
      return UserType.None;
    }
    case /\/customer*/.test(path): {
      return UserType.Customer;
    }

    default:
      return null;
  }
};

export const drawerMenuSlice = createSlice({
  name: 'drawerMenu',
  initialState,
  reducers: {
    setDrawerMenu: (state, action) => {
      state.menuItems = action.payload.menuItems || state.menuItems;
      state.title = action.payload.title || state.title;
      state.paletteType = action.payload.paletteType || state.paletteType;
      state.userType = action.payload.userType || state.userType;
      state.isCustomerPage = isCustomerPage(action.payload.path);
    },
    updateDrawerMenu: (state, action) => {
      const path = action.payload;
      const type = userType(path) || state.userType;
      state.userType = type;
      state.menuItems = drawerMenu(path);
      state.isCustomerPage = isCustomerPage(path);
    },
  },
});

export const { setDrawerMenu, updateDrawerMenu } = drawerMenuSlice.actions;
